import { Texture } from 'pixi.js';

/**
 * The title card's drawn pieces, each painted once on a canvas and shared:
 * white or grey, so a tint gives each colourway its colour.
 */

const cache = new Map<string, Texture>();

function painted(key: string, width: number, height: number, paint: (ctx: CanvasRenderingContext2D) => void): Texture {
  const hit = cache.get(key);
  if (hit) return hit;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (ctx) paint(ctx);
  const texture = Texture.from(canvas);
  cache.set(key, texture);
  return texture;
}

/** A sunburst of `count` soft rays, bright in the middle and fading out. */
export function raysTexture(count = 18): Texture {
  const size = 512;
  return painted(`rays:${count}`, size, size, (ctx) => {
    const c = size / 2;
    const fade = ctx.createRadialGradient(c, c, 0, c, c, c);
    fade.addColorStop(0, 'rgba(255,255,255,0.95)');
    fade.addColorStop(0.35, 'rgba(255,255,255,0.55)');
    fade.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = fade;
    const step = (Math.PI * 2) / count;
    for (let i = 0; i < count; i++) {
      const a = i * step;
      ctx.beginPath();
      ctx.moveTo(c, c);
      ctx.arc(c, c, c, a - step * 0.22, a + step * 0.22);
      ctx.closePath();
      ctx.fill();
    }
  });
}

/** A spotlight's beam: a narrow top widening to the bottom, brighter
 *  towards where it lands (where the pool takes over). The top centre is
 *  its source. */
export function beamTexture(): Texture {
  const width = 256;
  const height = 512;
  return painted('beam', width, height, (ctx) => {
    const down = ctx.createLinearGradient(0, 0, 0, height);
    down.addColorStop(0, 'rgba(255,255,255,0.3)');
    down.addColorStop(0.8, 'rgba(255,255,255,0.85)');
    down.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = down;
    ctx.beginPath();
    ctx.moveTo(width * 0.47, 0);
    ctx.lineTo(width * 0.53, 0);
    ctx.lineTo(width, height);
    ctx.lineTo(0, height);
    ctx.closePath();
    ctx.fill();
    // Soften the beam's edges.
    ctx.globalCompositeOperation = 'destination-in';
    const across = ctx.createLinearGradient(0, 0, width, 0);
    across.addColorStop(0, 'rgba(0,0,0,0)');
    across.addColorStop(0.3, 'rgba(0,0,0,1)');
    across.addColorStop(0.7, 'rgba(0,0,0,1)');
    across.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = across;
    ctx.fillRect(0, 0, width, height);
  });
}

/** A gold coin face on: a rim, a raised centre and a shine, drawn in its
 *  own colours (tint it white). */
export function coinTexture(): Texture {
  const size = 48;
  return painted('coin', size, size, (ctx) => {
    const c = size / 2;
    const r = c - 1;
    ctx.fillStyle = '#7a4a08';
    ctx.beginPath();
    ctx.arc(c, c, r, 0, Math.PI * 2);
    ctx.fill();
    const face = ctx.createRadialGradient(c * 0.7, c * 0.6, 1, c, c, r);
    face.addColorStop(0, '#fff6c8');
    face.addColorStop(0.45, '#ffd34d');
    face.addColorStop(1, '#c98a12');
    ctx.fillStyle = face;
    ctx.beginPath();
    ctx.arc(c, c, r - 2.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = 'rgba(122,74,8,0.55)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(c, c, r * 0.62, 0, Math.PI * 2);
    ctx.stroke();
    ctx.fillStyle = 'rgba(255,255,255,0.75)';
    ctx.beginPath();
    ctx.ellipse(c * 0.72, c * 0.62, r * 0.28, r * 0.14, -0.6, 0, Math.PI * 2);
    ctx.fill();
  });
}
